import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Header from "../Components/Header";
import Footer from "../Components/Footer";
import { getData } from "../Api";

const OrdersDetail = () => {
  const { id } = useParams();
  const [orderData, setOrderData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchOrderDetails = async () => {
      if (!id) return;

      setLoading(true);
      setError(null);
      setOrderData(null);

      try {
        const response = await getData(`/orders/${id}`);

        if (response.success) {
          setOrderData(response.data);
        } else {
          setError("Order not found. Please check your order ID.");
        }
      } catch (err) {
        console.error("Order fetch error:", err);
        setError("Something went wrong. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [id]);

  const getStatusBadge = (status) => {
    switch (status) {
      case "pending":
        return <span className="badge bg-warning text-dark">Pending</span>;
      case "shipped":
        return <span className="badge bg-info">Shipped</span>;
      case "in_transit":
        return <span className="badge bg-primary">In Transit</span>;
      case "delivered":
        return <span className="badge bg-success">Delivered</span>;
      case "cancelled":
        return <span className="badge bg-danger">Cancelled</span>;
      case "failed":
        return <span className="badge bg-dark">Failed</span>;
      case "rto":
        return <span className="badge bg-warning">RTO</span>;
      default:
        return <span className="badge bg-secondary">Unknown</span>;
    }
  };

  return (
    <>
      <main className="container-fluid">
        <Header />
        <div
          className="row pagebgimg py-3"
          style={{
            backgroundImage: "url('../assets/img/Breadcrumbs Banner (1).png')",
          }}
        >
          <h1 className="text-center text-success fw-bold">Order Details</h1>
        </div>

        <section className="pt-5 pb-5 mb-5 container">
          {loading && <p>Loading order details...</p>}
          {error && <div className="alert alert-danger">{error}</div>}

          {orderData && (
            <div className="card mb-4">
              <div className="card-header d-flex justify-content-between align-items-center">
                <h3 className="mb-0">Order Details</h3>
                <button
                  className="btn btn-success btn-sm"
                  onClick={() => navigate(-1)}
                >
                  Back
                </button>
              </div>

              <div className="card-body">
                {/* Products Table */}
                {Array.isArray(orderData.product_names) &&
                  orderData.product_names.length > 0 && (
                    <div className="table-responsive mb-4">
                      <table className="table text-nowrap">
                        <thead>
                          <tr>
                            <th>Item ID</th>
                            <th>Product Image</th>
                            <th>Name</th>
                            <th>Quantity</th>
                            <th>Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {orderData.product_names.map((name, index) => (
                            <tr key={index}>
                              <td>#{index + 1}</td>
                              <td>
                                <img
                                  src={
                                    orderData.product_images?.[index] ||
                                    "/default.png"
                                  }
                                  alt={name}
                                  width="50"
                                  height="50"
                                />
                              </td>
                              <td>{name}</td>
                              <td>
                                {orderData.product_quantities?.[index] || 0} Pcs
                              </td>
                              <td>
                                ₹
                                {(
                                  (parseFloat(
                                    orderData.product_prices?.[index]
                                  ) || 0) *
                                  (parseInt(
                                    orderData.product_quantities?.[index]
                                  ) || 0)
                                ).toFixed(2)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                <div className="row">
                  {/* Shipping Address */}
                  <div className="col-lg-5 mb-3">
                    <div className="p-4 shadow-sm rounded bg-light">
                      <h5>Shipping Address</h5>
                      <p>{orderData.name}</p>
                      <p>{orderData.phone}</p>
                      <p>{orderData.state}</p>
                      <p>{orderData.pincode}</p>
                      <p>{orderData.address}</p>
                    </div>
                  </div>

                  {/* Order Summary */}
                  <div className="col-lg-7 mb-3">
                    <div className="p-4 shadow-sm rounded bg-light">
                      <h5 className="fw-bold mb-3">Order Summary</h5>
                      <ul className="list-unstyled">
                        <li className="d-flex justify-content-between">
                          <span>Subtotal</span>
                          <span className="fw-bold">
                            ₹{orderData.total ?? 0}
                          </span>
                        </li>
                        {/* <li className="d-flex justify-content-between">
                          <span>Weight</span>
                          <span className="fw-bold">
                            {orderData.weight ?? 0} gm
                          </span>
                        </li>*/}
                        <li className="d-flex justify-content-between">
                          <span>Shipping</span>
                          <span className="text-success fw-bold">Free</span>
                        </li>
                        <li className="d-flex justify-content-between">
                          <span>Discount</span>
                          <span className="text-danger fw-bold">₹0</span>
                        </li>
                        <li className="d-flex justify-content-between">
                          <span>Tax</span>
                          <span className="fw-bold">₹0</span>
                        </li>
                        <li className="d-flex justify-content-between">
                          <span>Paid by</span>
                          <span className="fw-bold">
                            {orderData.paymentMethod ?? "N/A"}
                          </span>
                        </li>
                      </ul>
                      <hr />
                      <div className="d-flex justify-content-between fw-bold fs-5">
                        <span>Total</span>
                        <span>₹{orderData.total ?? 0}</span>
                      </div>
                    </div>
                  </div>

                  {/* Delivery Details */}
                  <div className="col-lg-12 mt-4">
                    <div className="p-4 shadow-sm rounded bg-light">
                      <h5 className="fw-bold mb-3">Delivery Details</h5>
                      <ul className="list-unstyled">
                        <li className="d-flex justify-content-between">
                          <span>Delivery Partner</span>
                          <span className="fw-bold text-capitalize">
                            {orderData.delivery_type ?? "N/A"}
                          </span>
                        </li>
                        <li className="d-flex justify-content-between">
                          <span>Tracking ID</span>
                          <span className="fw-bold">
                            {orderData.tracking_id ?? "Not Assigned"}
                          </span>
                        </li>
                        <li className="d-flex justify-content-between">
                          <span>Delivery Status</span>
                          <span>
                            {getStatusBadge(orderData.delivery_status)}
                          </span>
                        </li>
                      </ul>

                      {orderData.tracking_url && (
                        <a
                          href={orderData.tracking_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-outline-primary btn-sm mt-2"
                        >
                          Track Package
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </section>

        <Footer />
      </main>
    </>
  );
};

export default OrdersDetail;
